import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Por favor, preencha todos os campos');
      return;
    }

    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Bem-vindo, ${user.name}!`);
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Credenciais inválidas';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#E9E5D6] flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <span className="font-['Playfair_Display'] text-3xl font-bold text-[#0F5A34]">
              Portugal
            </span>
            <span className="font-['Playfair_Display'] text-3xl font-bold text-[#E53935] ml-1">
              à Vista
            </span>
          </Link>
        </div>

        {/* Form Card */}
        <div className="bg-white p-8 rounded-sm border border-[#D6D2C4] shadow-sm">
          <h1 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A1A1A] mb-2 text-center">
            Entrar
          </h1>
          <p className="text-[#5C5C5C] font-['Inter'] text-sm text-center mb-6">
            Acesse sua conta para continuar
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-['Montserrat'] text-sm font-medium">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                required
                className="border-[#D6D2C4]"
                data-testid="login-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="font-['Montserrat'] text-sm font-medium">
                Senha
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="border-[#D6D2C4] pr-10"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C5C5C] hover:text-[#1A1A1A]"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0F5A34] hover:bg-[#0A3F24] font-['Montserrat'] text-sm"
              data-testid="login-submit"
            >
              {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              Entrar
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#5C5C5C] font-['Inter'] text-sm">
              Não tem uma conta?{' '}
              <Link to="/registro" className="text-[#0F5A34] font-medium hover:underline">
                Criar conta
              </Link>
            </p>
          </div>

          {/* Demo credentials */}
          <div className="mt-6 p-4 bg-[#E9E5D6] rounded-sm">
            <p className="text-xs font-['Inter'] text-[#5C5C5C] text-center">
              <strong>Admin Demo:</strong> admin@portugalavista.com / admin123
            </p>
          </div>
        </div>

        {/* Back to home */}
        <div className="text-center mt-6">
          <Link to="/" className="text-[#5C5C5C] font-['Inter'] text-sm hover:text-[#0F5A34]">
            ← Voltar ao início
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
